import {
  auditUnsubscribe,
  checkEmailHasAddress,
} from "encharge-domain/lib/helpers/email_can_spam_compliance_helper";

/** What the editor says about an email whose sends are held. */
export interface CanSpamHold {
  /** one sentence: sending is on hold, and what the email lacks */
  headline: string;
  /** what to fix, one plain sentence each */
  reasons: string[];
}

/**
 * The verdict an email's sends are held by, for the app: the stored one,
 * except that "pending" holds nothing for an account whose sends the
 * CAN-SPAM check never holds (the account info's canSpamHolds: a trusted or
 * blocked account, one older than the address rule, or a stage with the
 * checks off, as the send path decides, F08). An account info without it
 * (an older server) is taken as held.
 */
export const verdictThatHolds = (
  verdict: IEmailContent["canSpamCompliance"] | undefined,
  account: { canSpamHolds?: boolean } | undefined
): IEmailContent["canSpamCompliance"] | undefined =>
  verdict === "pending" && account?.canSpamHolds === false
    ? undefined
    : verdict;

/** a whole-link tag written as a link's address: {{person.unsubscribeLink}} in an href */
const LINK_TAG_IN_HREF = /^\{\{-?\s*person\.(unsubscribeLink|unsubscribeAllLink|managePreferencesLink)\s*-?\}\}$/;

const URL_TAG_FOR: Record<string, string> = {
  unsubscribeLink: "{{person.unsubscribeURL}}",
  unsubscribeAllLink: "{{person.unsubscribeAllURL}}",
  managePreferencesLink: "{{person.managePreferencesURL}}",
};

const MAX_LINKS = 3;

const linkName = (label: string) => {
  const text = label.trim();
  if (!text) return "An image link";
  return `The "${text.length > 50 ? `${text.slice(0, 49)}...` : text}" link`;
};

/**
 * What the editor says when the email's stored CAN-SPAM verdict is "pending":
 * its sends are held until a save passes the check
 * (EmailService.recordCanSpamCompliance). The reasons are read from the
 * email as it is now, with the check the save uses (auditUnsubscribe); its
 * reason for each broken link is the `why` the audit gives. undefined when
 * sending is not held.
 */
export const describeCanSpamHold = ({
  verdict,
  html,
  plainText = false,
}: {
  verdict: IEmailContent["canSpamCompliance"] | undefined;
  html: string | undefined;
  plainText?: boolean;
}): CanSpamHold | undefined => {
  if (verdict !== "pending") return undefined;
  const body = String(html || "");
  const audit = auditUnsubscribe(body, { plainText });
  const noUnsubscribe = !audit.working;
  const noAddress = !checkEmailHasAddress(body);

  if (!noUnsubscribe && !noAddress) {
    // the email as it is now passes: the hold is from the last save
    return {
      headline:
        "Sending is on hold: when this email was last saved, it had no working unsubscribe link or no mailing address.",
      reasons: ["Save the email to check it again."],
    };
  }

  const reasons: string[] = [];
  if (noUnsubscribe) {
    const notes: string[] = [];
    for (const s of audit.suspects) {
      const name = linkName(s.label);
      const href = s.href.trim();
      const whole = LINK_TAG_IN_HREF.exec(href);
      const use = whole && URL_TAG_FOR[whole[1]];
      const note = use
        ? `${name} uses ${href} inside a link, which breaks it. Use ${use} instead.`
        : `${name} ${s.why}.`;
      if (!notes.includes(note)) notes.push(note);
    }
    reasons.push(...notes.slice(0, MAX_LINKS));
    const more =
      Math.max(0, notes.length - MAX_LINKS) + (audit.suspectsOmitted || 0);
    if (more) {
      reasons.push(
        `And ${more} more link${more === 1 ? "" : "s"} like ${
          more === 1 ? "it" : "these"
        }.`
      );
    }
    if (!notes.length) {
      reasons.push(
        audit.neverShown
          ? // there, but hidden or in Liquid that never renders (V2-L5)
            "The unsubscribe link is never shown to the reader: it is hidden, or inside Liquid that never renders. Show it to every reader."
          : plainText
          ? "Add an unsubscribe link: insert {{person.unsubscribeURL}} in the text."
          : "Add an unsubscribe link: link your unsubscribe text to {{person.unsubscribeURL}}, or insert the {{person.unsubscribeLink}} merge tag in the text."
      );
    }
  }
  if (noAddress) {
    reasons.push(
      "Add your mailing address to the footer with the {{account.mailingAddress}} merge tag."
    );
  }

  const lacks =
    noUnsubscribe && noAddress
      ? "no working unsubscribe link and no mailing address"
      : noUnsubscribe
      ? "no working unsubscribe link"
      : "no mailing address";
  return { headline: `Sending is on hold: this email has ${lacks}.`, reasons };
};
